import {ApiClient} from '@k2/common/backend/api-client';
import {K2CostingOptionsData, TransitItemType} from '../../entities-state/types';
import {values} from 'ramda';
import {map, take} from 'rxjs/operators';
import {TransitBundle} from '@k2/staff/content/clients/client/assignment/hhg/costing/quotes-review/transit-bundles/transit-bundles.component';
import {Observable} from 'rxjs';
import {toFormSpecNormalizer} from '@k2/common/k2-forms/utils';
import {RatesPayload} from '@k2/common/backend/endpoints/rates-engine-endpoint';

export class AssignmentMultipleHhgEndpoint {
  constructor(
    private api: ApiClient
  ) {

  }

  // Overview
  fetchHhgs = (assignmentId: number) => {
    return this.api.getWithoutCache(`/assignment/${assignmentId}/asi-hhg-shipment`);
  };

  addNewHhgToAssignment(assignmentId: number) {
    return this.api.post(`/assignment/${assignmentId}/asi-hhg-shipment`, null);
  }

  fetchInitiationOverviewByHhgId = (hhgId: number) => {
    return this.api.get(`/asi-hhg-shipment/${hhgId}/overview/initiation`);
  };

  // Addresses
  fetchAddressesByHhgId = (hhgId: number) => {
    return this.api.get(`/asi-hhg-shipment/${hhgId}/overview/assignee-address`);
  };

  getFormOriginAssigneeAddress = (hhgId: number) => {
    return this.api.get(`/asi-hhg-shipment/${hhgId}/overview/assignee-address/origin/form`)
      .pipe(map(payload => payload.assignee_origin_address_form));
  };

  upsertOriginAddressByHhgId = (hhgId: number, data: any) => {
    return this.api.post(`/asi-hhg-shipment/${hhgId}/overview/assignee-address/origin`, data);
  };

  getFormUpdateDestinationAssigneeAddress = (hhgId: number, addressId: number) => {
    return this.api.get(`/asi-hhg-shipment/${hhgId}/overview/assignee-address/destination/${addressId}/form-update`)
      .pipe(map(payload => payload.assignee_destination_address_form));
  };

  getFormCreateAssigneeDestinationAddress = (hhgId: number) => {
    return this.api.get(`/asi-hhg-shipment/${hhgId}/overview/assignee-address/destination/form-create`)
      .pipe(map(payload => payload.assignee_destination_address_form));
  };

  createDestinationAddressByHhgId = (hhgId: number, data: any) => {
    return this.api.post(`/asi-hhg-shipment/${hhgId}/overview/assignee-address/destination`, data);
  };

  updateDestinationAddressByHhgId = (hhgId: number, addressId: number, data: any) => {
    return this.api.patch(`/asi-hhg-shipment/${hhgId}/overview/assignee-address/destination/${addressId}`, data);
  };

  deleteDestinationAddressByHhgId = (hhgId: number, addressId: number) => {
    return this.api.delete(`/asi-hhg-shipment/${hhgId}/overview/assignee-address/destination/${addressId}`);
  };

  getStorageAddressFormByHhgId = (hhgId: number) => {
    return this.api.get(`/asi-hhg-shipment/${hhgId}/overview/storage-address/form`)
      .pipe(map(payload => payload.partner_form));
  };

  createStorageAddressByHhgId = (hhgId: number, data: any) => {
    return this.api.post(`/asi-hhg-shipment/${hhgId}/overview/storage-address`, data);
  };

  updateStorageAddressByHhgId = (hhgId: number, data: any) => {
    return this.api.patch(`/asi-hhg-shipment/${hhgId}/overview/storage-address`, data);
  };

  removeStorageAddressByHhgId = (hhgId: number) => {
    return this.api.delete(`/asi-hhg-shipment/${hhgId}/overview/storage-address`);
  };

  // Transits
  fetchTransitsByHhgIdForOverview = (hhgId: number) => {
    return this.api.get(`/asi-hhg-shipment/${hhgId}/overview/transit`);
  };

  fetchTransitsByHhgIdForPackingMaterials = (hhgId: number) => {
    return this.api.get(`/asi-hhg-shipment/${hhgId}/packing-materials/transit`);
  };

  getFormCreateTransit = (hhgId: number) => {
    return this.api
      .get(`/asi-hhg-shipment/${hhgId}/overview/transit/form-create`)
      .pipe(map(payload => payload.form));
  };

  fetchTransitEditFormByHhgId = (hhgId: number, transitId: string) => {
    return this.api
      .get(`/asi-hhg-shipment/${hhgId}/overview/transit/${transitId}/form-update`)
      .pipe(map(payload => payload.form));
  };

  createTransitByHhgId = (hhgId: number, data: any) => {
    return this.api.post(`/asi-hhg-shipment/${hhgId}/overview/transit`, data);
  };

  updateTransitByHhgId = (hhgId: number, transitId: string, data: any) => {
    return this.api.patch(
      `/asi-hhg-shipment/${hhgId}/overview/transit/${transitId}`,
      data
    );
  };

  updateTransitsByHhgId = (hhgId: number, data: any) => {
    return this.api.put(`/asi-hhg-shipment/${hhgId}/overview/transit`, data);
  };

  deleteTransitByHhgId = (hhgId: number, transitId: string) => {
    return this.api.delete(
      `/asi-hhg-shipment/${hhgId}/overview/transit/${transitId}`
    );
  };

  // Surveys
  fetchSurveyByHhgId = (hhgId: number) => {
    return this.api.get(`/asi-hhg-shipment/${hhgId}/survey`);
  };

  updateSurveyByHhgId = (hhgId: number, data: any) => {
    return this.api.patch(
      `/asi-hhg-shipment/${hhgId}/survey`,
      data
    );
  };

  updateSurveyDatesByHhgId = (hhgId: number, data: any) => {
    return this.api.patch(
      `/asi-hhg-shipment/${hhgId}/survey/dates`,
      data
    );
  };

  fetchNewSurveyInitiationFormByHhgId = (hhgId: number, partnerId: number) => {
    return this.api
      .get(`/asi-hhg-shipment/${hhgId}/survey/form-initiate/${partnerId}`)
      .pipe(map(payload => payload.form));
  };

  initiateSurveyByHhgId = (hhgId: number, data: any) => {
    return this.api.post(
      `/asi-hhg-shipment/${hhgId}/survey`,
      data
    );
  };

  markSurveyAsInitiatedByHhgId = (hhgId: number, partnerId: number) => {
    return this.api.post(
      `/asi-hhg-shipment/${hhgId}/survey/off-platform/${partnerId}`,
      null
    );
  };

  fetchNewSurveyFormByHhgId = (hhgId: number) => {
    return this.api
      .get(`/asi-hhg-shipment/${hhgId}/survey/additional/form-create`)
      .pipe(map(payload => payload.form));
  };

  createSurveyByHhgId = (hhgId: number, data: any) => {
    return this.api.post(`/asi-hhg-shipment/${hhgId}/survey/additional`, data);
  };

  listSurveyProperties = (hhgId: number) => {
    return this.api.get(`/asi-hhg-shipment/${hhgId}/survey/property`);
  };

  updateSurveyProperty = (hhgId: number, addressId: number | string, data: any) => {
    return this.api.patch(
      `/asi-hhg-shipment/${hhgId}/survey/property/${addressId}`,
      data
    );
  };

  listSurveyVolumes = (hhgId: number) => {
    return this.api.get(`/asi-hhg-shipment/${hhgId}/survey/volume`);
  };

  updateSurveyVolumeMeasurementSystem = (hhgId: number, measurementSystemId: number) => {
    const data = {
      measurement_scale_types_id: measurementSystemId
    };

    return this.api.patch(
      `/asi-hhg-shipment/${hhgId}/survey/volume/measurement-system`,
      data
    );
  };

  updateSurveyVolumesGrossFactor = (hhgId: number, grossPercent: number | null) => {
    const data = {
      gross_calc_factor_percent: grossPercent
    };

    return this.api.patch(
      `/asi-hhg-shipment/${hhgId}/survey/volume/gross-factor`,
      data
    );
  };

  updateSurveyTransitVolumes = (hhgId: number, transitId: string, data: any) => {
    return this.api.patch(
      `/asi-hhg-shipment/${hhgId}/survey/transit/${transitId}/volume`,
      data
    );
  };

  listSurveyTransitPhotos = (hhgId: number, transitId: string) => {
    return this.api
      .get(
        `/asi-hhg-shipment/${hhgId}/survey/transit/${transitId}/photo`
      ).pipe(
        map(payload => payload.documents),
        map(values)
      );
  };

  saveSurveyTransitPhotos = (hhgId: number, transitId: string, ids: string | number[]) => {
    const data = { selected_documents: ids };

    return this.api.put(
      `/asi-hhg-shipment/${hhgId}/survey/transit/${transitId}/photo`,
      data
    );
  };

  deleteSurveyTransitPhoto = (hhgId: number, transitId: string, photoId: string) => {
    return this.api.delete(
      `/asi-hhg-shipment/${hhgId}/survey/transit/${transitId}/photo/${photoId}`
    );
  };

  getSurveyTransitItemFormCreate = (hhgId: number, transitId: string, itemType: TransitItemType) => {
    return this.api.get(
      `/asi-hhg-shipment/${hhgId}/survey/transit/${transitId}/item/${itemType}/form-create`
    )
      .pipe(
        map(payload => payload.form)
      );
  };

  createSurveyTransitItem = (hhgId: number, transitId: string, itemType: TransitItemType, data: any) => {
    return this.api.post(
      `/asi-hhg-shipment/${hhgId}/survey/transit/${transitId}/item/${itemType}`,
      data
    );
  };

  getSurveyTransitItemFormUpdate = (hhgId: number, transitId: string, itemType: TransitItemType, itemId: string) => {
    return this.api.get(
      `/asi-hhg-shipment/${hhgId}/survey/transit/${transitId}/item/${itemType}/${itemId}/form-update`
    )
      .pipe(
        map(payload => payload.form)
      );
  };

  updateSurveyTransitItem = (hhgId: number, transitId: string, itemType: TransitItemType, itemId: string, data: any) => {
    return this.api.patch(
      `/asi-hhg-shipment/${hhgId}/survey/transit/${transitId}/item/${itemType}/${itemId}`,
      data
    );
  };

  deleteSurveyTransitItem = (hhgId: number, transitId: string, itemType: TransitItemType, itemId: string) => {
    return this.api.delete(
      `/asi-hhg-shipment/${hhgId}/survey/transit/${transitId}/item/${itemType}/${itemId}`
    );
  };

  getSurveyTransitItemTypeFormReplicate = (hhgId: number, transitId: string, itemType: TransitItemType) => {
    return this.api.get(
      `/asi-hhg-shipment/${hhgId}/survey/transit/${transitId}/item-type/${itemType}/form-replicate`
    )
      .pipe(map(payload => payload.form));
  };

  replicateSurveyTransitItemType = (hhgId: number, transitId: string, itemType: TransitItemType, data: { transits_list: any, items: any }) => {
    return this.api.post(`/asi-hhg-shipment/${hhgId}/survey/transit/${transitId}/item-type/${itemType}/replicate`,
      data);
  };

  getSurveySpecialistServiceFormCreate = (hhgId: number) => {
    return this.api
      .get(`/asi-hhg-shipment/${hhgId}/survey/specialist-service/form-create`)
      .pipe(
        map(payload => payload.form)
      );
  };

  getSurveySpecialistServiceFormUpdate = (hhgId: number, specialistServiceId: string) => {
    return this.api
      .get(`/asi-hhg-shipment/${hhgId}/survey/specialist-service/${specialistServiceId}/form-update`)
      .pipe(
        map(payload => payload.form)
      );
  };

  listSurveySpecialistServices = (hhgId: number) => {
    return this.api.get(`/asi-hhg-shipment/${hhgId}/survey/specialist-service`);
  };

  createSurveySpecialistService = (hhgId: number, data: any) => {
    return this.api
      .post(`/asi-hhg-shipment/${hhgId}/survey/specialist-service`, data)
      .pipe(
        map(payload => payload.specialist_services)
      );
  };

  updateSurveySpecialistService = (hhgId: number, specialistServiceId: string, data: any) => {
    return this.api
      .patch(
        `/asi-hhg-shipment/${hhgId}/survey/specialist-service/${specialistServiceId}`,
        data
      )
      .pipe(
        map(payload => payload.specialist_services)
      );
  };

  deleteSurveySpecialistService = (hhgId: number, specialistServiceId: string) => {
    return this.api.delete(`/asi-hhg-shipment/${hhgId}/survey/specialist-service/${specialistServiceId}`);
  };

  submitSurveyVolumesForCosting = (hhgId: number) => {
    const data = { survey_is_locked: true };

    return this.api.post(
      `/asi-hhg-shipment/${hhgId}/survey/volume/submit-for-costing`,
      data
    );
  };

  generateSurveyVolumesCratesPdf = (hhgId: number) => {
    const data = {};

    return this.api.post(
      `/asi-hhg-shipment/${hhgId}/survey/volume/generate-crates-pdf`,
      data
    );
  };

  getCostingConfigurations = (hhgId: number) => {
    return this.api.get(`/asi-hhg-shipment/${hhgId}/costing/configuration`);
  };

  updateCostingConfigurations = (hhgId: number, data: any) => {
    return this.api.put(`/asi-hhg-shipment/${hhgId}/costing/configuration`, data);
  };

  getCostingFormAddPartner = (hhgId: number) => {
    return this.api.get(`/asi-hhg-shipment/${hhgId}/costing/partner/form-add`)
      .pipe(
        map(payload => payload.form)
      );
  };

  addCostingPartner = (hhgId: number, data: any) => {
    return this.api.post(`/asi-hhg-shipment/${hhgId}/costing/partner`, data);
  };

  deleteAllCostingRequests = (hhgId: number) => {
    return this.api.delete(`/asi-hhg-shipment/${hhgId}/costing/request`);
  };

  deleteCostingRequest = (hhgId: number, costRequestId: string) => {
    return this.api.delete(
      `/asi-hhg-shipment/${hhgId}/costing/request/${costRequestId}`
    );
  };

  getCostingRequestFormGenerate = (hhgId: number, costRequestId: string) => {
    return this.api
      .get(
        `/asi-hhg-shipment/${hhgId}/costing/request/${costRequestId}/form-generate`
      )
      .pipe(map(payload => payload.forms));
  };

  updateCostingRequestSection = (hhgId: number, data: { container_id: string; cost_request_id: number } & any) => {
    return this.api.patch(
      `/asi-hhg-shipment/${hhgId}/costing/request/${data.cost_request_id}/section/${data.container_id}`,
      data
    );
  };

  sendCostingRequestEmail = (hhgId: number, emailId: number) => {
    return this.api.post(
      `/asi-hhg-shipment/${hhgId}/costing/request/email/${emailId}/send`,
      null
    );
  };

  listCostingVendorServices(hhgId: number, vendorId: number) {
    return this.api
      .get(`/asi-hhg-shipment/${hhgId}/costing/vendor/${vendorId}/service`)
      .pipe(map(payload => payload.transits));
  }

  allocateCostingVendorServices(hhgId: number, vendorId: number, data: any) {
    return this.api.put(
      `/asi-hhg-shipment/${hhgId}/costing/vendor/${vendorId}/service`,
      data
    );
  }

  getQuoteOverview = (hhgId: number) => {
    return this.api.get(`/asi-hhg-shipment/${hhgId}/quote/overview`);
  };

  getVendorQuoteFormUpdate = (vendorQuoteId: number) => {
    return this.api.get(
      `/vendor-quote/${vendorQuoteId}/form-update`
    );
  };

  updateVendorQuote = (vendorQuoteId: number, data: any) => {
    return this.api.patch(
      `/vendor-quote/${vendorQuoteId}`,
      data
    );
  };

  getQuotesOptionsStatus = (hhgId: number) => {
    return this.api.get(
      `/asi-hhg-shipment/${hhgId}/quote/option/status`
    );
  };

  generateQuotesOptions = (hhgId: number, data: any) => {
    return this.api.post(
      `/asi-hhg-shipment/${hhgId}/quote/option/generate`,
      data
    );
  };

  listQuotesOptions = (hhgId: number) => {
    return this.api.get(`/asi-hhg-shipment/${hhgId}/quote/option`);
  };

  updateQuotesOptionsRounding = (hhgId: number, rounding: any) => {
    return this.api.patch(
      `/asi-hhg-shipment/${hhgId}/quote/option/rounding`,
      {
        rounding
      }
    );
  };

  updateQuotesOptionsLabels = (hhgId: number, labels: any) => {
    return this.api.patch(
      `/asi-hhg-shipment/${hhgId}/quote/option/label`, labels
    ).pipe(take(1));
  };

  deleteQuotesOptions = (hhgId: number) => {
    return this.api.delete(`/asi-hhg-shipment/${hhgId}/quote/option`);
  };

  getVendorQuoteOptionsBundleFormCreate = (hhgId: number) => {
    return this.api.get(`/asi-hhg-shipment/${hhgId}/quote/option/bundle/form-create`)
      .pipe(take(1), map(({ form }) => form));
  };

  createVendorQuoteOptionsBundle = (hhgId: number, data: TransitBundle) => {
    return this.api.post(`/asi-hhg-shipment/${hhgId}/quote/option/bundle`, data);
  };

  getVendorQuoteOptionsBundleFormUpdate = (hhgId: number, bundleId: string) => {
    return this.api.get(`/asi-hhg-shipment/${hhgId}/quote/option/bundle/${bundleId}/form-update`)
      .pipe(take(1), map(({ form }) => form));
  };

  updateVendorQuoteOptionsBundle = (hhgId: number, data: TransitBundle) => {
    return this.api.patch(`/asi-hhg-shipment/${hhgId}/quote/option/bundle/${data.id}`, data);
  };

  deleteVendorQuoteOptionsBundle = (hhgId: number, bundleId: string) => {
    return this.api.delete(`/asi-hhg-shipment/${hhgId}/quote/option/bundle/${bundleId}`);
  };

  getVendorQuoteOptionsTransitFormUpdate(hhgId: number, transitId: number) {
    return this.api.get(`/asi-hhg-shipment/${hhgId}/quote/option/transit/${transitId}/form-update`)
      .pipe(map(({ form }) => form));
  }

  updateVendorQuoteOptionsTransit(hhgId: number, transitId: number, data) {
    return this.api.patch(`/asi-hhg-shipment/${hhgId}/quote/option/transit/${transitId}`, data);
  }

  updateVendorQuoteOptionsPolicy = (hhgId: number, data: any) => {
    return this.api.patch(`/asi-hhg-shipment/${hhgId}/quote/option/policy`, data);
  };

  generateVendorQuoteOptionsPdf = (hhgId: number, data: any) => {
    return this.api.post(`/asi-hhg-shipment/${hhgId}/quote/option/generate-pdf`, data);
  };

  approveVendorQuoteOptions = (hhgId: number) => {
    return this.api.post(`/asi-hhg-shipment/${hhgId}/quote/option/approve`, null);
  };

  regenerateVendorQuoteOptionsPdf = (hhgId: number, data: any) => {
    return this.api.post(`/asi-hhg-shipment/${hhgId}/quote/option/regenerate-pdf`, data);
  };

  getVendorQuoteOptionsEmailForm = (hhgId: number) => {
    return this.api
      .get(`/asi-hhg-shipment/${hhgId}/quote/option/email/form`)
      .pipe(map(payload => payload.form));
  };

  sendVendorQuoteOptionsEmail = (hhgId: number, data: any) => {
    return this.api.post(
      `/asi-hhg-shipment/${hhgId}/quote/option/email`,
      data
    );
  };

  getVendorQuoteOptionsApprovalForm = (hhgId: number) => {
    return this.api
      .get(`/asi-hhg-shipment/${hhgId}/quote/option/approval/form`)
      .pipe(map(payload => payload.form));
  };

  confirmVendorQuoteOptionsApproval = (hhgId: number, data: any) => {
    return this.api.post(
      `/asi-hhg-shipment/${hhgId}/quote/option/approval`,
      data
    );
  };

  getVendorQuoteOptionsRejectionForm(hhgId: number) {
    return this.api
      .get(`/asi-hhg-shipment/${hhgId}/quote/option/rejection/form`)
      .pipe(map(payload => payload.form));
  }

  confirmVendorQuoteOptionsRejection(hhgId: number, data: any) {
    return this.api.post(
      `/asi-hhg-shipment/${hhgId}/quote/option/rejection`,
      data
    );
  }

  listVendorQuoteOptionsConfirmations(hhgId: number) {
    return this.api.get(`/asi-hhg-shipment/${hhgId}/quote/option/confirmation`);
  }

  approveVendorQuoteOptionsConfirmations(hhgId: number, data: any) {
    return this.api.post(
      `/asi-hhg-shipment/${hhgId}/quote/option/confirmation/approve`,
      data
    );
  }

  completeVendorQuoteOptionsConfirmations(hhgId: number, data: any) {
    return this.api.post(
      `/asi-hhg-shipment/${hhgId}/quote/option/confirmation/complete`,
      data
    );
  }

  deleteVendorQuoteOptionsConfirmation(hhgId: number, confirmationId: string) {
    return this.api.delete(
      `/asi-hhg-shipment/${hhgId}/quote/option/confirmation/${confirmationId}`
    );
  }

  getVendorQuoteOfferForm(offerHash: string) {
    return this.api.get(`/vendor-quote/offer/${offerHash}/form`).pipe(take(1), map(response => response.form));
  }

  updateVendorQuoteOffer(offerHash: string, email: string) {
    return this.api.patch(`/vendor-quote/offer/${offerHash}`, email).pipe(take(1), map(res => ({
      ...res.offer,
      specialist_services: Object.values(res.offer.specialist_services)
    })));
  }

  getVendorQuoteOfferApprovalForm(offerHash: string) {
    return this.api.get(`/vendor-quote/offer/${offerHash}/approval/form`)
      .pipe(take(1), map(res => res.form));
  }

  updateVendorQuoteOfferApproval(offerHash: string, data) {
    return this.api.patch(`/vendor-quote/offer/${offerHash}/approval`, data)
      .pipe(take(1), map(res => res.offer));
  }

  generateSingleSupplierConfirmationEmail(hhgId: number, supplierConfirmationId: string) {
    return this.api.post(
      `/asi-hhg-shipment/${hhgId}/costing/supplier-confirmation/single/${supplierConfirmationId}/email`,
      {}
    );
  }

  generateMultipleSuppliersConfirmationsEmails(hhgId: number, suppliersConfirmationsIds: string) {
    return this.api.post(
      `/asi-hhg-shipment/${hhgId}/costing/supplier-confirmation/multiple/email`,
      {
        suppliersConfirmationsIds: suppliersConfirmationsIds.split(',')
      }
    );
  }

  genVendorSupplierConfirmationEmailHtmlPreview(hhgId: number, vendorSupplierConfirmationEmailId: number): Observable<string> {
    return this.api.get(`/asi-hhg-shipment/${hhgId}/costing/vendor-supplier-confirmation-email/${vendorSupplierConfirmationEmailId}/html-preview`)
      .pipe(map(payload => payload.html));
  }

  updateSingleVendorSupplierConfirmationEmailSection(hhgId: number, data, vendorSupplierConfirmationEmailId: number) {
    return this.api.patch(
      `/asi-hhg-shipment/${hhgId}/costing/vendor-supplier-confirmation-email/single/${vendorSupplierConfirmationEmailId}/section/${data.container_id}`,
      data
    );
  }

  updateMultipleVendorSupplierConfirmationEmailsSection(hhgId: number, data, vendorSupplierConfirmationEmailsIds: number[]) {
    return this.api.patch(
      `/asi-hhg-shipment/${hhgId}/costing/vendor-supplier-confirmation-email/multiple/section/${data.container_id}`,
      {
        ...data,
        vendorSupplierConfirmationEmailsIds
      }
    );
  }

  queueSingleVendorSupplierConfirmationEmail(hhgId: number, vendorSupplierConfirmationEmailId: number) {
    return this.api.post(`/asi-hhg-shipment/${hhgId}/costing/vendor-supplier-confirmation-email/single/${vendorSupplierConfirmationEmailId}/queue`, null);
  }

  queueMultipleVendorSupplierConfirmationEmails(hhgId: number, vendorSupplierConfirmationEmailsIds: number[]) {
    return this.api.post(`/asi-hhg-shipment/${hhgId}/costing/vendor-supplier-confirmation-email/multiple/queue`,
      { vendorSupplierConfirmationEmailsIds }
    );
  }

  generateVendorSupplierConfirmationEmailDocument(hhgId: number, vendorSupplierConfirmationEmailId: number, documentType: string) {
    return this.api.post(
      `/asi-hhg-shipment/${hhgId}/costing/vendor-supplier-confirmation-email/${vendorSupplierConfirmationEmailId}/document/${documentType}/generate`,
      {}
    );
  }

  createVendorSupplierConfirmationEmailSectionOriginAddress(hhgId: number, data: any, vendorSupplierConfirmationEmailId: number) {
    return this.api.post(
      `/asi-hhg-shipment/${hhgId}/costing/vendor-supplier-confirmation-email/${vendorSupplierConfirmationEmailId}/section/origin-address`,
      data
    );
  }

  sendVendorQuoteOfferToWorkspace(offerHash: string) {
    return this.api.post(
      `/vendor-quote/offer/${offerHash}/send-to-workspace`,
      null
    );
  }

  getVendorQuoteOptionsCosting(hhgId: number) {
    return this.api.get(`/asi-hhg-shipment/${hhgId}/quote/option/costing`);
  }

  updateVendorQuoteOptionsCosting(hhgId: number, data: K2CostingOptionsData) {
    return this.api.patch(`/asi-hhg-shipment/${hhgId}/quote/option/costing`, data);
  }

  updateVendorQuoteOptionsCostingRounding(hhgId: number, rounding: number) {
    return this.api.patch(`/asi-hhg-shipment/${hhgId}/quote/option/costing/rounding`, { rounding });
  }

  getVendorQuoteTransitInsuranceFormCreate(hhgId: number, transitId: string) {
    return this.api.get(
      `/asi-hhg-shipment/${hhgId}/quote/transit/${transitId}/insurance/form-create`
    );
  }

  createVendorQuoteTransitInsurance(hhgId: number, transitId: string, data: any) {
    return this.api.post(
      `/asi-hhg-shipment/${hhgId}/quote/transit/${transitId}/insurance`,
      data
    );
  }

  getVendorQuoteTransitInsuranceFormUpdate(hhgId: number, transitId: string, vendorQuoteInsuranceId: number) {
    return this.api.get(
      `/asi-hhg-shipment/${hhgId}/quote/transit/${transitId}/insurance/${vendorQuoteInsuranceId}/form-update`
    );
  }

  updateVendorQuoteTransitInsurance(hhgId: number, transitId: string, vendorQuoteInsuranceId: number, data: any) {
    return this.api.patch(
      `/asi-hhg-shipment/${hhgId}/quote/transit/${transitId}/insurance/${vendorQuoteInsuranceId}`,
      data
    );
  }

  deleteVendorQuoteTransitInsurance(hhgId: number, transitId: string, vendorQuoteInsuranceId: number) {
    return this.api.delete(
      `/asi-hhg-shipment/${hhgId}/quote/transit/${transitId}/insurance/${vendorQuoteInsuranceId}`
    );
  }

  getVendorQuoteRates(hhgId: number, vendorQuoteId: number, transitId: string, productIds: string[]) {
    return <Observable<RatesPayload>>this.api.get(
      `/asi-hhg-shipment/${hhgId}/quote/${vendorQuoteId}/rates?transitId=${transitId}&productIds=${productIds.join(',')}`
    );
  };

  getVendorQuoteTransitProductAdjustmentForm(hhgId: number, vendorQuoteId: number, transitId: string, productKey: string) {
    return this.api.get(
      `/asi-hhg-shipment/${hhgId}/quote/${vendorQuoteId}/transit/${transitId}/product/${productKey}/adjustment/form`
    );
  }

  updateVendorQuoteTransitProductAdjustment(hhgId: number, vendorQuoteId: number, transitId: string, productKey: string, data: any) {
    return this.api.patch(
      `/asi-hhg-shipment/${hhgId}/quote/${vendorQuoteId}/transit/${transitId}/product/${productKey}/adjustment`,
      data
    );
  }

  acceptMove4uSurvey(hhgId: number, move4uSurveyId: number) {
    return this.api.post(
      `/asi-hhg-shipment/${hhgId}/survey/move4u-survey/${move4uSurveyId}/accept`,
      null
    );
  }

  rejectMove4uSurvey = (hhgId: number, move4uSurveyId: number) => {
    return this.api.post(
      `/asi-hhg-shipment/${hhgId}/survey/move4u-survey/${move4uSurveyId}/reject`,
      null
    );
  };

  updateTransitAdditionalDetails(hhgId: number, transitId: string, data: any) {
    return this.api.patch(
      `/asi-hhg-shipment/${hhgId}/transit/${transitId}/additional-details`,
      data
    );
  };

  getTrackingOverview = (hhgId: number) => {
    return this.api.get(`/asi-hhg-shipment/${hhgId}/overview/tracking`);
  };

  toggleTransitForClosing = (hhgId: number, transitId: string, data) => {
    return this.api.patch(
      `/asi-hhg-shipment/${hhgId}/transit/${transitId}/toggle-for-closing`,
      data
    );
  };

  updateTransitOriginServices(hhgId: number, transitId: string, data) {
    return this.api.patch(
      `/asi-hhg-shipment/${hhgId}/transit/${transitId}/origin-services`,
      data
    );
  };

  updateTransitDestinationServices(hhgId: number, transitId: string, data) {
    return this.api.patch(
      `/asi-hhg-shipment/${hhgId}/transit/${transitId}/destination-services`,
      data
    );
  };

  getAdditionalCostFormCreate(hhgId: number) {
    return this.api
      .get(`/asi-hhg-shipment/${hhgId}/transit/additional-cost/form-create`)
      .pipe(map(payload => payload.form));
  };

  createAdditionalCost(hhgId: number, transitId: string, data) {
    return this.api
      .post(`/asi-hhg-shipment/${hhgId}/transit/${transitId}/additional-cost`, data)
      .pipe(map(payload => payload.form));
  };

  updateSpecialistService(hhgId: number, serviceInstanceId: string, data) {
    return this.api.patch(
      `/asi-hhg-shipment/${hhgId}/transit/specialist-service/${serviceInstanceId}`,
      data
    );
  };

  getStorageOverview(hhgId: number) {
    return this.api
      .get(`/asi-hhg-shipment/${hhgId}/storage`)
      .pipe(map(payload => payload.storage));
  };

  getStorageForm(hhgId: number) {
    return this.api
      .get(`/asi-hhg-shipment/${hhgId}/storage/form`)
      .pipe(map(payload => payload.form));
  };

  createStorage(hhgId: number, data: any) {
    return this.api.post(`/asi-hhg-shipment/${hhgId}/storage`, data);
  };

  deleteStorage(hhgId: number) {
    return this.api.delete(`/asi-hhg-shipment/${hhgId}/storage`);
  };

  getStorageLocationRevisionFormCreate(hhgId: number, storageLocation: string) {
    return this.api
      .get(`/asi-hhg-shipment/${hhgId}/storage/${storageLocation}/revision/form-create`)
      .pipe(map(payload => payload.form));
  };

  createStorageLocationRevision(hhgId: number, storageLocation: string, data: any) {
    return this.api.post(
      `/asi-hhg-shipment/${hhgId}/storage/${storageLocation}/revision`,
      data
    );
  };

  getStorageLocationRevisionFormUpdate(hhgId: number, storageLocation: string, revisionId: string) {
    return this.api
      .get(`/asi-hhg-shipment/${hhgId}/storage/${storageLocation}/revision/${revisionId}`)
      .pipe(map(payload => payload.form));
  };

  updateStorageLocationRevision(hhgId: number, storageLocation: string, revisionId: string, data: any) {
    return this.api.patch(
      `/asi-hhg-shipment/${hhgId}/storage/${storageLocation}/revision/${revisionId}`,
      data
    );
  };

  deleteStorageLocationRevision(hhgId: number, storageLocation: string, revisionId: string) {
    return this.api.delete(
      `/asi-hhg-shipment/${hhgId}/storage/${storageLocation}/revision/${revisionId}`
    );
  };

  getStorageLocationInspectionFormCreate(hhgId: number, storageLocation: string) {
    return this.api
      .get(`/asi-hhg-shipment/${hhgId}/storage/${storageLocation}/inspection/form-create`)
      .pipe(map(payload => payload.form));
  };

  createStorageLocationInspection(hhgId: number, storageLocation: string, data: any) {
    return this.api.post(
      `/asi-hhg-shipment/${hhgId}/storage/${storageLocation}/inspection`,
      data
    );
  };

  getStorageLocationInspectionFormUpdate(hhgId: number, storageLocation: string, inspectionId: string) {
    return this.api
      .get(`/asi-hhg-shipment/${hhgId}/storage/${storageLocation}/inspection/${inspectionId}/form-update`)
      .pipe(map(payload => payload.form));
  };

  updateStorageLocationInspection(hhgId: number, storageLocation: string, inspectionId: string, data: any) {
    return this.api.patch(
      `/asi-hhg-shipment/${hhgId}/storage/${storageLocation}/inspection/${inspectionId}`,
      data
    );
  };

  deleteStorageLocationInspection(hhgId: number, storageLocation: string, inspectionId: string) {
    return this.api.delete(
      `/asi-hhg-shipment/${hhgId}/storage/${storageLocation}/inspection/${inspectionId}`
    );
  };

  getStorageLocationBillingFormCreate(hhgId: number, storageLocation: string) {
    return this.api
      .get(`/asi-hhg-shipment/${hhgId}/storage/${storageLocation}/billing/form-create`)
      .pipe(map(payload => payload.form));
  };

  createStorageLocationBilling(hhgId: number, storageLocation: string, data: any) {
    return this.api.post(
      `/asi-hhg-shipment/${hhgId}/storage/${storageLocation}/billing`,
      data
    );
  };

  getStorageLocationBillingFormUpdate(hhgId: number, storageLocation: string, billingId: string) {
    return this.api
      .get(`/asi-hhg-shipment/${hhgId}/storage/${storageLocation}/billing/${billingId}/form-update`)
      .pipe(map(payload => payload.form));
  };

  patchStorageLocationBilling(hhgId: number, storageLocation: string, billingId: string, data: any) {
    return this.api.patch(
      `/asi-hhg-shipment/${hhgId}/storage/${storageLocation}/billing/${billingId}`,
      data
    );
  };

  deleteStorageLocationBilling(hhgId: number, storageLocation: string, billingId: string) {
    return this.api.delete(
      `/asi-hhg-shipment/${hhgId}/storage/${storageLocation}/billing/${billingId}`
    );
  };

  getInsuranceOverview(hhgId: number) {
    return this.api
      .get(`/asi-hhg-shipment/${hhgId}/insurance`)
      .pipe(map(payload => payload.insurance));
  };

  getPendingActionFormCreate(hhgId: number) {
    return this.api
      .get(`/asi-hhg-shipment/${hhgId}/insurance/pending-action/form-create`)
      .pipe(map(payload => payload.form));
  };

  createPendingAction(hhgId: number, data) {
    return this.api.post(
      `/asi-hhg-shipment/${hhgId}/insurance/pending-action`,
      data
    );
  };

  getPendingActionFormUpdate(hhgId: number, pendingActionId: string) {
    return this.api
      .get(`/asi-hhg-shipment/${hhgId}/insurance/pending-action/${pendingActionId}/form-update`)
      .pipe(map(payload => payload.form));
  };

  updatePendingAction(hhgId: number, pendingActionId: string, data) {
    return this.api.patch(
      `/asi-hhg-shipment/${hhgId}/insurance/pending-action/${pendingActionId}`,
      data
    );
  };

  deletePendingAction(hhgId: number, pendingActionId: string) {
    return this.api
      .delete(`/asi-hhg-shipment/${hhgId}/insurance/pending-action/${pendingActionId}`)
      .pipe(map(payload => payload.insurance));
  };

  getPendingActionCertificateForm(hhgId: number, pendingActionId: string) {
    return this.api
      .get(`/asi-hhg-shipment/${hhgId}/insurance/pending-action/${pendingActionId}/certificate/form`)
      .pipe(map(payload => payload.form));
  };

  createPendingActionCertificate(hhgId: number, pendingActionId: string, data) {
    return this.api.post(
      `/asi-hhg-shipment/${hhgId}/insurance/pending-action/${pendingActionId}/certificate`,
      data
    );
  };

  getTransitActionFormCreate(hhgId: number, transitId: string) {
    return this.api
      .get(`/asi-hhg-shipment/${hhgId}/insurance/transit/${transitId}/action/form-create`)
      .pipe(map(payload => payload.form));
  };

  createTransitAction(hhgId: number, transitId: string, data) {
    return this.api.post(
      `/asi-hhg-shipment/${hhgId}/insurance/transit/${transitId}/action`,
      data
    );
  };

  getTransitActionFormUpdate(hhgId: number, transitId: string, actionId: string) {
    return this.api
      .get(`/asi-hhg-shipment/${hhgId}/insurance/transit/${transitId}/action/${actionId}/form-update`)
      .pipe(map(payload => payload.form));
  };

  updateTransitAction(hhgId: number, transitId: string, actionId: string, data) {
    return this.api.patch(
      `/asi-hhg-shipment/${hhgId}/insurance/transit/${transitId}/action/${actionId}`,
      claimFormNormalizer.forBackend(data)
    );
  };

  getClaimFormCreate(hhgId: number) {
    return this.api
      .get(`/asi-hhg-shipment/${hhgId}/insurance/claim/form-create`)
      .pipe(map(payload => payload.form));
  };

  createClaim(hhgId: number, data) {
    return this.api.post(
      `/asi-hhg-shipment/${hhgId}/insurance/claim`,
      claimFormNormalizer.forBackend(data)
    );
  };

  getClaimFormUpdate(hhgId: number, claimId: string) {
    return this.api
      .get(`/asi-hhg-shipment/${hhgId}/insurance/claim/${claimId}/form-update`)
      .pipe(
        map(payload => payload.form),
        map(claimFormNormalizer.forFrontend)
      );
  };

  updateClaim(hhgId: number, claimId: string, data) {
    return this.api.patch(
      `/asi-hhg-shipment/${hhgId}/insurance/claim/${claimId}`,
      claimFormNormalizer.forBackend(data)
    );
  };

  deleteClaim(hhgId: number, claimId: string) {
    return this.api
      .delete(`/asi-hhg-shipment/${hhgId}/insurance/claim/${claimId}`)
      .pipe(map(payload => payload.insurance));
  };

  getVendorQcOverview(hhgId: number) {
    return this.api
      .get(`/asi-hhg-shipment/${hhgId}/vendor-qc`)
      .pipe(map(payload => payload.data));
  };

  getVendorQcFormCreate(hhgId: number) {
    return this.api
      .get(`/asi-hhg-shipment/${hhgId}/vendor-qc/form-create`)
      .pipe(map(payload => payload.data));
  };

  createVendorQc(hhgId: number, data) {
    return this.api
      .post(`/asi-hhg-shipment/${hhgId}/vendor-qc`, data);
  };

  getVendorQcFormUpdate(vendorHhgQcId: number) {
    return this.api
      .get(`/vendor-qc/${vendorHhgQcId}/form-update`)
      .pipe(map(payload => payload.data));
  };

  updateVendorQc(vendorHhgQcId: number, data) {
    return this.api.patch(
      `/vendor-qc/${vendorHhgQcId}`,
      data
    );
  };

  deleteVendorQc(vendorHhgQcId: number) {
    return this.api.delete(
      `/vendor-qc/${vendorHhgQcId}`
    );
  };
}

const claimFormNormalizer = toFormSpecNormalizer('origin_agent_id', 'destination_agent_id');
